import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Mail, CheckCircle } from "lucide-react";
import "leaflet/dist/leaflet.css";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 25 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const LOCATIONS = [
  {
    name: "Schreinerei Loos",
    address: "Lorenkamp 4, 45721 Haltern am See",
    phone: "02364 / 5042567",
    tel: "023645042567",
    color: "#8B5E3C",
    lat: 51.7411,
    lng: 7.1888,
    label: "Werkstatt",
  },
  {
    name: "Bestattungshaus Loos",
    address: "Mühlenstraße 6, 45721 Haltern am See",
    phone: "02364 / 2384",
    tel: "023642384",
    color: "#3E4C59",
    lat: 51.7395,
    lng: 7.1872,
    label: "Bestattungshaus",
  },
];

const Map = () => {
  const mapRef = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    import("leaflet").then((L) => {
      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([51.7403, 7.1880], 15);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      // Force proper tile rendering after dynamic load
      setTimeout(() => map.invalidateSize(), 200);

      LOCATIONS.forEach((loc) => {
        const icon = L.divIcon({
          className: "",
          html: `<div style="background:${loc.color};color:white;padding:5px 10px;border-radius:2px;font-family:Manrope,sans-serif;font-size:11px;font-weight:600;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.25);letter-spacing:0.05em">${loc.label}</div>`,
          iconAnchor: [loc.label.length * 3.5, 10],
        });
        L.marker([loc.lat, loc.lng], { icon })
          .addTo(map)
          .bindPopup(
            `<b style="font-family:Playfair Display,serif;font-size:14px">${loc.name}</b><br/><span style="font-size:12px;color:#666">${loc.address}</span><br/><a href="tel:${loc.tel}" style="color:${loc.color};font-size:12px">${loc.phone}</a>`
          );
      });
    });

    return () => {};
  }, []);

  return <div ref={mapRef} style={{ height: "460px", width: "100%" }} data-testid="map-container" />;
};

const Kontakt = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", bereich: "Schreinerei", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <main className="pt-20">
      {/* PAGE HEADER */}
      <section className="py-16 bg-[#F8F5F0]" data-testid="kontakt-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p {...fadeUp(0)} className="uppercase tracking-[0.3em] text-xs text-[#8B5E3C] font-manrope mb-3">
            Kontakt
          </motion.p>
          <motion.h1 {...fadeUp(0.1)} className="font-playfair text-4xl sm:text-5xl text-[#2B2B2B]">
            Wir sind für Sie da.
          </motion.h1>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[#F8F5F0]" data-testid="map-section">
        <motion.div {...fadeUp(0)} className="shadow-inner">
          <Map />
        </motion.div>
      </section>

      {/* ADDRESSES + FORM */}
      <section className="py-20 bg-[#F8F5F0]" data-testid="kontakt-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Addresses */}
            <div className="lg:col-span-2 space-y-6">
              {LOCATIONS.map((loc, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="bg-white border-l-4 p-6 shadow-sm"
                  style={{ borderLeftColor: loc.color }}
                  data-testid={`address-card-${i}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] font-manrope mb-1" style={{ color: loc.color }}>
                    {loc.label}
                  </div>
                  <h3 className="font-playfair text-xl text-[#2B2B2B] mb-3">{loc.name}</h3>
                  <div className="space-y-2 text-sm font-manrope text-[#2B2B2B]/65">
                    <div className="flex items-start gap-2.5">
                      <MapPin size={14} className="mt-0.5 shrink-0" style={{ color: loc.color }} />
                      <span>{loc.address}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Phone size={14} className="shrink-0" style={{ color: loc.color }} />
                      <a href={`tel:${loc.tel}`} className="hover:text-[#2B2B2B] transition-colors font-semibold">
                        {loc.phone}
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* 24/7 Emergency */}
              <motion.div
                {...fadeUp(0.25)}
                className="bg-[#3E4C59] p-6"
                data-testid="emergency-contact-card"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] font-manrope text-[#C9A66B] mb-1">Notfall</div>
                <h3 className="font-playfair text-xl text-white mb-2">24/7 Trauerfall</h3>
                <a href="tel:+491714312224" className="text-2xl font-playfair text-[#C9A66B] hover:text-white transition-colors block">
                  0171 / 4312224
                </a>
                <p className="text-white/55 text-xs font-manrope mt-2">Rund um die Uhr erreichbar.</p>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-3 bg-white border border-[#8B5E3C]/10 p-8"
              data-testid="contact-form-container"
            >
              {sent ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center" data-testid="form-success">
                  <CheckCircle size={48} className="text-[#8B5E3C] mb-4" />
                  <h3 className="font-playfair text-2xl text-[#2B2B2B] mb-3">Vielen Dank!</h3>
                  <p className="text-[#2B2B2B]/60 font-manrope text-sm">
                    Ihre Nachricht ist bei uns eingegangen. Wir melden uns schnellstmöglich bei Ihnen.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="font-playfair text-2xl text-[#2B2B2B] mb-6">Schreiben Sie uns</h2>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-manrope uppercase tracking-widest text-[#2B2B2B]/50 mb-1.5">Name *</label>
                        <input
                          required
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Ihr Name"
                          className="w-full border border-[#8B5E3C]/20 bg-[#F8F5F0] px-4 py-3 text-sm font-manrope text-[#2B2B2B] focus:outline-none focus:border-[#8B5E3C] transition-colors"
                          data-testid="form-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-manrope uppercase tracking-widest text-[#2B2B2B]/50 mb-1.5">E-Mail *</label>
                        <input
                          required
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="ihre@email.de"
                          className="w-full border border-[#8B5E3C]/20 bg-[#F8F5F0] px-4 py-3 text-sm font-manrope text-[#2B2B2B] focus:outline-none focus:border-[#8B5E3C] transition-colors"
                          data-testid="form-email"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-manrope uppercase tracking-widest text-[#2B2B2B]/50 mb-1.5">Telefon</label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="02364 / ..."
                          className="w-full border border-[#8B5E3C]/20 bg-[#F8F5F0] px-4 py-3 text-sm font-manrope text-[#2B2B2B] focus:outline-none focus:border-[#8B5E3C] transition-colors"
                          data-testid="form-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-manrope uppercase tracking-widest text-[#2B2B2B]/50 mb-1.5">Bereich *</label>
                        <select
                          value={form.bereich}
                          onChange={(e) => setForm({ ...form, bereich: e.target.value })}
                          className="w-full border border-[#8B5E3C]/20 bg-[#F8F5F0] px-4 py-3 text-sm font-manrope text-[#2B2B2B] focus:outline-none focus:border-[#8B5E3C] transition-colors"
                          data-testid="form-bereich"
                        >
                          <option value="Schreinerei">Schreinerei</option>
                          <option value="Bestattungen">Bestattungen</option>
                          <option value="Allgemein">Allgemein</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-manrope uppercase tracking-widest text-[#2B2B2B]/50 mb-1.5">Nachricht *</label>
                      <textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Wie können wir Ihnen helfen?"
                        className="w-full border border-[#8B5E3C]/20 bg-[#F8F5F0] px-4 py-3 text-sm font-manrope text-[#2B2B2B] focus:outline-none focus:border-[#8B5E3C] transition-colors resize-none"
                        data-testid="form-message"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-[#8B5E3C] text-white py-4 text-sm font-manrope font-semibold tracking-wide hover:bg-[#7a5235] transition-colors"
                      data-testid="form-submit"
                    >
                      Nachricht senden
                    </button>
                    <p className="text-[#2B2B2B]/40 text-xs font-manrope text-center">
                      Im dringenden Trauerfall rufen Sie uns bitte direkt an: 0171 4312224
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Kontakt;
